import { TokenListItem } from "@eulerxyz/core-sdk"
import { getAddress } from "viem"
import { getFallbackTokenList } from "./utils"

// Type for tokens from the external API
interface ApiToken {
  chainId: number
  address: string
  name: string
  symbol: string
  decimals: number
  logoURI?: string
}

// API response can be either an array or an object with tokens property
interface ApiResponse {
  tokens?: ApiToken[]
}

// Union type for merged token (can be either fallback or API token)
type MergedToken = TokenListItem

export const fetchTokenList = async (baseApiUrl: string, chainId: number) => {
  const headers = {
    "x-skip-cache": "true",
  }
  const fallbackTokenList = getFallbackTokenList(chainId)

  let apiTokenList: ApiToken[] = []
  try {
    const response = await fetch(`${baseApiUrl}/v1/tokens?chainId=${chainId}`, {
      headers,
    })

    if (response.ok) {
      const apiData: ApiToken[] | ApiResponse = await response.json()
      apiTokenList = Array.isArray(apiData) ? apiData : apiData.tokens || []
    }
  } catch (apiError) {
    console.warn(
      "Failed to fetch tokens from API, using fallback only:",
      apiError,
    )
  }

  // Merge token lists, avoiding duplicates based on address
  const tokenMap = new Map<string, MergedToken>()

  // Add fallback tokens first
  fallbackTokenList.forEach((token: TokenListItem) => {
    if (token.addressInfo) {
      tokenMap.set(getAddress(token.addressInfo), {
        chainId: token.chainId,
        addressInfo: token.addressInfo,
        name: token.name,
        symbol: token.symbol,
        decimals: token.decimals,
        logoURI: token.logoURI,
      })
    }
  })

  // Add API tokens, overwriting fallback if same address
  // If API token doesn't have logoURI, use it from fallback token
  apiTokenList.forEach((token: ApiToken) => {
    const address = token.address
    if (address) {
      const normalizedAddress = getAddress(address)
      const existingFallbackToken = tokenMap.get(normalizedAddress) as
        | TokenListItem
        | undefined

      // Merge logoURI from fallback if API token doesn't have it
      const mergedToken: MergedToken = {
        chainId: token.chainId,
        decimals: token.decimals,
        name: token.name,
        symbol: token.symbol,
        addressInfo: getAddress(token.address),
        logoURI: token.logoURI || existingFallbackToken?.logoURI || "",
      }

      tokenMap.set(normalizedAddress, mergedToken)
    }
  })

  const mergedTokenList: MergedToken[] = Array.from(tokenMap.values())

  return mergedTokenList
}
